import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  InputAdornment,
  Slide,
  Grow,
  useMediaQuery,
  useTheme,
  Chip,
  Stack,
} from "@mui/material";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

export default function EmployeeManager() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const defaultForm = {
    name: "",
    email: "",
    type: "employee",
    rateType: "hourly",
    rate: "",
    taxCredits: "3750",
    cutoff: "44000",
  };

  const [form, setForm] = useState(defaultForm);

  const fetchEmployees = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(collection(db, "employees"), where("uid", "==", user.uid));
    const snapshot = await getDocs(q);
    setEmployees(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      newErrors.email = "Invalid email";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const data = {
      ...form,
      rate: form.rate ? parseFloat(form.rate) : null,
      taxCredits: form.taxCredits ? parseFloat(form.taxCredits) : 3750,
      cutoff: form.cutoff ? parseFloat(form.cutoff) : 44000,
    };

    if (editing) {
      await updateDoc(doc(db, "employees", editing.id), data);
    } else {
      await addDoc(collection(db, "employees"), {
        ...data,
        createdAt: new Date(),
        uid: auth.currentUser.uid,
      });
    }

    setOpen(false);
    setEditing(null);
    setForm(defaultForm);
    setErrors({});
    fetchEmployees();
  };

  const handleEdit = (emp) => {
    setEditing(emp);
    setForm({
      name: emp.name,
      email: emp.email,
      type: emp.type,
      rateType: emp.rateType || "hourly",
      rate: emp.rate || "",
      taxCredits: emp.taxCredits || "3750",
      cutoff: emp.cutoff || "44000",
    });
    setOpen(true);
  };

  const handleDelete = async () => {
    await deleteDoc(doc(db, "employees", confirmDelete.id));
    setConfirmDelete(null);
    fetchEmployees();
  };

  const filteredEmployees = employees.filter((emp) =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box p={isMobile ? 1 : 2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ mb: { xs: 1, sm: 0 } }}>
          Employee Manager
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setForm(defaultForm);
            setEditing(null);
            setOpen(true);
          }}
          size="large"
        >
          + Add Employee
        </Button>
      </Box>

      <TextField
        fullWidth
        placeholder="Search employees..."
        variant="outlined"
        margin="dense"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          borderRadius: 2,
          backgroundColor:
            searchTerm.length > 0
              ? theme.palette.action.hover
              : "background.paper",
          transition: "background 0.2s",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Grow in>
        <Paper elevation={2} sx={{ borderRadius: 3, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {filteredEmployees.length === 0
              ? "No employees found"
              : "Employee List"}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List>
            {filteredEmployees.map((emp, index) => (
              <React.Fragment key={emp.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <IconButton onClick={() => handleEdit(emp)} color="info">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setConfirmDelete(emp)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  }
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: index % 2 === 0 ? "background.default" : "white",
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight={600}>{emp.name}</Typography>
                        {emp.rate && (
                          <Chip
                            label={
                              emp.rateType === "salary"
                                ? `€${emp.rate}/year`
                                : `€${emp.rate}/${emp.rateType}`
                            }
                            size="small"
                            sx={{
                              background: "#dbeafe",
                              fontWeight: 500,
                              fontSize: 13,
                              ml: 0.5,
                            }}
                          />
                        )}
                        <Chip
                          label={
                            emp.type === "contractor"
                              ? "Contractor"
                              : "Employee"
                          }
                          size="small"
                          color={
                            emp.type === "contractor" ? "warning" : "success"
                          }
                          sx={{
                            ml: 0.5,
                            fontSize: 13,
                            fontWeight: 500,
                            opacity: 0.82,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography color="text.secondary">
                        {emp.email}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < filteredEmployees.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Grow>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 22 }}>
          {editing ? "Edit Employee" : "Add Employee"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField
              fullWidth
              label="Name"
              margin="dense"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
            />
            <TextField
              fullWidth
              label="Email"
              margin="dense"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              select
              fullWidth
              label="Type"
              margin="dense"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <MenuItem value="employee">Employee</MenuItem>
              <MenuItem value="contractor">Contractor</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              label="Rate Type"
              margin="dense"
              value={form.rateType}
              onChange={(e) => setForm({ ...form, rateType: e.target.value })}
            >
              <MenuItem value="hourly">Hourly</MenuItem>
              <MenuItem value="salary">Salary</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Rate (€) – Optional"
              value={form.rate}
              onChange={(e) => setForm({ ...form, rate: e.target.value })}
            />
            
            <Typography variant="subtitle2" sx={{ pt: 1, fontWeight: 600 }}>
              Tax Details (2026)
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Tax Credits (€)"
                type="number"
                margin="dense"
                value={form.taxCredits}
                onChange={(e) => setForm({ ...form, taxCredits: e.target.value })}
                helperText="Annual Total (e.g. 3750)"
              />
              <TextField
                fullWidth
                label="Rate Cut-off (€)"
                type="number"
                margin="dense"
                value={form.cutoff}
                onChange={(e) => setForm({ ...form, cutoff: e.target.value })}
                helperText="Standard Rate Band (e.g. 44000)"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 20 }}>
          Delete Employee
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{confirmDelete?.name}</strong>
          ?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
